import { FC, ReactNode } from 'react';
import { Button, ChatBubble, Skeleton } from 'react-daisyui';
import AppLogo from '@/components/ui/AppLogo/AppLogo.tsx';
import dayjs from 'dayjs';
import { cn } from '@/utils/classNames.ts';
import Markdown from 'marked-react';

type MessageBotProps = {
  message: ReactNode;
  date?: string;
  action?: () => void;
  actionText?: string;
  renderUnderMessage?: ReactNode;
};

const MessageBot: FC<MessageBotProps> = ({ message, date, action, actionText, renderUnderMessage }) => {
  return (
    <div className="flex justify-start">
      <ChatBubble className={cn('lg:w-10/12 max-w-full', { 'mb-16': !!action })}>
        <ChatBubble.Header>
          BrandSnag AI {date ? <ChatBubble.Time>{dayjs.utc(date).format('HH:mm')}</ChatBubble.Time> : null}
        </ChatBubble.Header>
        <ChatBubble.Avatar>
          <div className="flex justify-center items-center bg-white">
            <AppLogo.LogoOnly className="w-7 h-7" />
          </div>
        </ChatBubble.Avatar>
        <ChatBubble.Message className="prose relative">
          {typeof message === 'string' ? <Markdown>{message}</Markdown> : message}
          {action ? (
            <Button
              onClick={action}
              className="absolute top-[calc(100%+15px)] left-1/2 translate-x-[-50%]"
              color="primary"
            >
              {actionText ?? null}
            </Button>
          ) : null}
        </ChatBubble.Message>
        {renderUnderMessage ? (
          <div className="mt-5 col-span-2 flex flex-col gap-4 w-full">{renderUnderMessage}</div>
        ) : null}
      </ChatBubble>
    </div>
  );
};

type MessageUserProps = {
  name: string;
  message: ReactNode;
  date: string;
};

const MessageUser: FC<MessageUserProps> = ({ name, message, date }) => {
  return (
    <div className="flex justify-end">
      <ChatBubble end className="lg:w-10/12 max-w-full">
        <ChatBubble.Header>
          {name} <ChatBubble.Time>{dayjs.utc(date).format('HH:mm')}</ChatBubble.Time>
        </ChatBubble.Header>
        <ChatBubble.Avatar innerClassName="bg-secondary-400" />
        <ChatBubble.Message className="prose">
          {typeof message === 'string' ? <Markdown>{message}</Markdown> : message}
        </ChatBubble.Message>
      </ChatBubble>
    </div>
  );
};

type MessageSkeletonProps = {
  end?: boolean;
};
const MessageSkeleton: FC<MessageSkeletonProps> = ({ end }) => {
  return (
    <div className={cn('flex', { 'justify-end': end })}>
      <div className="flex items-end gap-2 w-9/12 max-w-full py-1">
        <Skeleton className={cn('w-10 h-10 rounded-full', { 'order-2': end })} />
        <div className={cn('flex grow gap-2 flex-col', { 'order-1': end, 'items-end': end })}>
          <Skeleton className="w-20 h-4" />
          <Skeleton className="flex w-full h-20" />
        </div>
      </div>
    </div>
  );
};

export default {
  Bot: MessageBot,
  User: MessageUser,
  Skeleton: MessageSkeleton,
};
